import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import PhoneMockup from "@/components/PhoneMockup";
import WaitlistForm from "@/components/WaitlistForm";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function Hero() {
  const { config } = useSiteConfig();
  const t = config.t.hero;
  const HEAD = [t.line1, t.line2];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);
  const glowScale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);

  return (
    <section ref={ref} id="top" className="mesh-bg relative overflow-hidden pt-32 pb-20 sm:pt-40" data-testid="hero-section">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-10">
        {/* left: text + CTAs + form */}
        <div className="min-w-0">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mb-6 inline-flex max-w-full items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-4 py-1.5 text-xs font-semibold text-brand">
            <span className="relative flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ai opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-ai" /></span>
            <span className="truncate">{t.badge}</span>
          </motion.div>

          <h1 className="text-5xl font-bold leading-[1.02] tracking-tighter text-neutral-900 sm:text-6xl lg:text-7xl">
            {HEAD.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.12em]">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                  {i === 1 ? <span className="text-gradient-blue">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-lg text-lg leading-relaxed text-neutral-500">
            {t.paragraph}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-3">
            <a href="#early-access" className="rounded-full bg-brand px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-brand/25 transition-transform hover:bg-brand-dark active:scale-95" data-testid="hero-cta-primary">{t.ctaPrimary}</a>
            <a href="#how-it-works" className="rounded-full border border-black/10 bg-white/60 px-7 py-3.5 text-base font-semibold text-neutral-800 backdrop-blur transition-colors hover:bg-white" data-testid="hero-cta-secondary">{t.ctaSecondary}</a>
          </motion.div>

          <motion.div id="early-access" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="glass mt-10 max-w-md rounded-3xl p-6 shadow-[0_20px_60px_rgba(0,40,120,0.12)]" data-testid="hero-waitlist">
            <h3 className="text-xl font-bold tracking-tight text-neutral-900">{t.formTitle}</h3>
            <p className="mb-4 mt-1 text-sm text-neutral-500">{t.formSubtitle}</p>
            <WaitlistForm source="hero" />
          </motion.div>
        </div>

        {/* right: floating phone */}
        <div className="flex justify-center lg:justify-end">
          <motion.div style={{ y: phoneY, rotate: phoneRotate }} className="relative">
            <motion.div style={{ scale: glowScale }} className="brand-glow absolute inset-0 -z-10 scale-125" />
            <div className="animate-float"><PhoneMockup screen="home" /></div>
          </motion.div>
        </div>
      </div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
        className="mx-auto mt-14 max-w-6xl px-6 text-center text-sm font-medium text-neutral-400 lg:text-left">
        {t.supporting}
      </motion.p>
    </section>
  );
}
